/**
 * Work Log API 호출 커스텀 Hook
 */

import { useState, useEffect, useCallback } from 'react'
import { AxiosError } from 'axios'
import apiClient from '../api/apiClient'
import { WorkLog, WorkLogStatus, WorkLogCategory } from '../constants/workLog'
import { sortWorkLogs } from '../utils/workLogHelpers'

interface UseWorkLogsOptions {
  customerId?: number | 'all'
  status?: WorkLogStatus | 'all'
  category?: WorkLogCategory | 'all'
}

export function useWorkLogs(options: UseWorkLogsOptions = {}) {
  const [workLogs, setWorkLogs] = useState<WorkLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchWorkLogs = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      let url = '/api/work-log'

      // 우선순위: customerId > status > category
      if (options.customerId && options.customerId !== 'all') {
        url = `/api/work-log/customer/${options.customerId}`
      } else if (options.status && options.status !== 'all') {
        url = `/api/work-log/status/${options.status}`
      } else if (options.category && options.category !== 'all') {
        url = `/api/work-log/category/${options.category}`
      }

      console.log('[useWorkLogs] Fetching from:', url, 'with options:', options)
      const response = await apiClient.get<WorkLog[]>(url)
      const workLogsData = Array.isArray(response.data) ? response.data : []

      // 정렬: IN_PROGRESS 우선, 최신순
      const sortedWorkLogs = sortWorkLogs(workLogsData)

      setWorkLogs(sortedWorkLogs)
    } catch (err) {
      const message = err instanceof AxiosError
        ? err.response?.data?.message || err.message
        : '알 수 없는 오류'
      setError(message)
      console.error('Failed to fetch work logs:', err)
      setWorkLogs([])
    } finally {
      setLoading(false)
    }
  }, [options.customerId, options.status, options.category])

  useEffect(() => {
    fetchWorkLogs()
  }, [fetchWorkLogs])

  return {
    workLogs,
    loading,
    error,
    refetch: fetchWorkLogs
  }
}

/**
 * Work Log 생성 Hook
 */
export function useCreateWorkLog() {
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const createWorkLog = async (data: {
    customerId: number
    category: WorkLogCategory
    title: string
    description: string
    expectedMinutes: number | null
  }): Promise<boolean> => {
    try {
      setCreating(true)
      setError(null)

      await apiClient.post('/api/work-log', data)
      return true
    } catch (err) {
      const message = err instanceof AxiosError
        ? err.response?.data?.message || 'Work Log 생성에 실패했습니다.'
        : '알 수 없는 오류'
      setError(message)
      console.error('Failed to create work log:', err)
      return false
    } finally {
      setCreating(false)
    }
  }

  return {
    createWorkLog,
    creating,
    error
  }
}

/**
 * Work Log 완료 Hook
 */
export function useCompleteWorkLog() {
  const [completing, setCompleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const completeWorkLog = async (id: number): Promise<boolean> => {
    try {
      setCompleting(true)
      setError(null)

      await apiClient.put(`/api/work-log/${id}/complete`)
      return true
    } catch (err) {
      const message = err instanceof AxiosError
        ? err.response?.data?.message || 'Work Log 완료 처리에 실패했습니다.'
        : '알 수 없는 오류'
      setError(message)
      console.error('Failed to complete work log:', err)
      return false
    } finally {
      setCompleting(false)
    }
  }

  return {
    completeWorkLog,
    completing,
    error
  }
}

/**
 * Work Log 삭제 Hook
 */
export function useDeleteWorkLog() {
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const deleteWorkLog = async (id: number): Promise<boolean> => {
    try {
      setDeleting(true)
      setError(null)

      await apiClient.delete(`/api/work-log/${id}`)
      return true
    } catch (err) {
      const message = err instanceof AxiosError
        ? err.response?.data?.message || 'Work Log 삭제에 실패했습니다.'
        : '알 수 없는 오류'
      setError(message)
      console.error('Failed to delete work log:', err)
      return false
    } finally {
      setDeleting(false)
    }
  }

  return {
    deleteWorkLog,
    deleting,
    error
  }
}
